import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageContainer from "../../v2/components/PageContainer";
import Pagination from "../../v2/components/Pagination";
import SearchInput from "../../v2/components/filters/SearchInput";
import DateRangeFilter from "../../v2/components/filters/DateRangeFilter";
import ChipSingleSelect from "../../v2/components/filters/ChipSingleSelect";
import { DownLoadTable } from "../../utils/DownloadtablesInExcel";
import SheetTable from "./components/SheetTable";
import CoveragePanel from "./components/CoveragePanel";
import { fetchExportRows, useTrackingColumns, useTrackingCoverage, useTrackingOrders } from "./hooks";
import { AVAILABILITY_META, buildExport } from "./format";

// Order Tracking Sheet — the ops team's Excel tracker, read live from the DB.
//
// Two tabs: the Sheet (every column of the ops sheet, in the sheet's own order, with
// not-yet-captured columns shown as hatched "not captured" cells so the gap is visible
// rather than silently dropped) and Column coverage (which columns the DB has, partly
// has, or lacks, with live fill rates). Column definitions come from the API's catalog,
// so this page carries no copy of what each column means.

const VIEW_OPTIONS = [
    { id: "CONFIRMED", label: "Paid or dispatched" },
    { id: "PENDING_DISPATCH", label: "Paid, not dispatched" },
    { id: "DISPATCHED", label: "Dispatched" },
    { id: "DELIVERED", label: "Delivered" },
    { id: "ALL", label: "All orders" },
];

const SOURCE_OPTIONS = ["", "META", "WEBSITE", "APP", "WATI", "VISIT", "TATVAPRACTICE", "GLP_1", "NUTRAPACK", "GOODFLIP", "REFERENCE", "INTERNAL", "ACKO", "LABSTACK"];

const PAGE_SIZES = [25, 50, 100, 200];

const PARAM = { tab: "tab", from: "from", to: "to", view: "view", q: "q", source: "source", page: "page", size: "size", missing: "missing" };

const isoDay = (date) => date.toISOString().slice(0, 10);

function OrderTrackingSheet() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [exporting, setExporting] = useState(false);

    const tab = searchParams.get(PARAM.tab) === "coverage" ? "coverage" : "sheet";
    const defaultFrom = useMemo(() => isoDay(new Date(Date.now() - 30 * 86400000)), []);
    const filters = {
        from: searchParams.get(PARAM.from) || defaultFrom,
        to: searchParams.get(PARAM.to) || "",
        view: searchParams.get(PARAM.view) || "CONFIRMED",
        search: searchParams.get(PARAM.q) || "",
        source: searchParams.get(PARAM.source) || "",
    };
    const page = Math.max(parseInt(searchParams.get(PARAM.page), 10) || 1, 1);
    const size = PAGE_SIZES.includes(parseInt(searchParams.get(PARAM.size), 10)) ? parseInt(searchParams.get(PARAM.size), 10) : 50;
    const showMissing = searchParams.get(PARAM.missing) !== "0";

    const updateParams = (updates, { keepPage = false } = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([k, v]) => (v === "" || v === null || v === undefined ? next.delete(k) : next.set(k, v)));
        if (!keepPage) next.delete(PARAM.page);
        setSearchParams(next, { replace: true });
    };

    const columnsQuery = useTrackingColumns();
    const ordersQuery = useTrackingOrders(filters, { page, size });
    const coverageQuery = useTrackingCoverage(filters, { enabled: tab === "coverage" });

    const allColumns = columnsQuery.data?.columns || [];
    const sheetColumns = showMissing ? allColumns : allColumns.filter((c) => c.availability !== "MISSING");
    const rows = ordersQuery.data?.rows || [];
    const total = ordersQuery.data?.pagination?.total || 0;
    const missingCount = allColumns.filter((c) => c.availability === "MISSING").length;

    const handleExport = async () => {
        setExporting(true);
        try {
            const data = await fetchExportRows(filters);
            const { header, body } = buildExport(allColumns, data.rows);
            DownLoadTable(`order-tracking-${filters.from}${filters.to ? `-to-${filters.to}` : ""}`, "Tracking", header, body);
            if (data.truncated) toast.warn(`Export capped at ${data.maxRows.toLocaleString("en-IN")} rows — narrow the date range for the rest.`);
        } catch (err) {
            toast.error(err.message || "Export failed");
        } finally {
            setExporting(false);
        }
    };

    return (
        <PageContainer>
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-sans text-2xl font-bold text-black">Order Tracking Sheet</h1>
                    <p className="mt-1 max-w-[640px] font-sans text-sm text-gray-500">
                        The ops tracker, live from PillEngine. Every sheet column is here in the same order. Hatched
                        cells are columns we don&apos;t capture in the database yet; hover a header to see where its value
                        comes from.
                    </p>
                </div>
                <div className="flex items-center gap-1 rounded-xl bg-[white] p-1 shadow-soft-xs" role="tablist">
                    {[
                        { id: "sheet", label: "Sheet" },
                        { id: "coverage", label: "Column coverage" },
                    ].map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            role="tab"
                            aria-selected={tab === t.id}
                            onClick={() => updateParams({ [PARAM.tab]: t.id === "sheet" ? "" : t.id }, { keepPage: true })}
                            className={`rounded-lg px-4 py-2 font-sans text-sm font-bold transition ${
                                tab === t.id ? "bg-elm-500 text-[white]" : "text-gray-600 hover:bg-gray-100"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </header>

            <section aria-label="Filters" className="mt-4 flex flex-wrap items-end gap-4">
                <div className="min-w-[260px] flex-1">
                    <SearchInput
                        value={filters.search}
                        onChange={(value) => updateParams({ [PARAM.q]: value })}
                        placeholder="Search order ID, patient, invoice no, AWB, pincode, phone"
                    />
                </div>
                <DateRangeFilter
                    from={filters.from}
                    to={filters.to}
                    idPrefix="tracking-order-date"
                    fromLabel="Order date from"
                    toLabel="Order date to"
                    onChange={(range) => updateParams({ [PARAM.from]: range.from, [PARAM.to]: range.to })}
                />
                <div>
                    <label htmlFor="tracking-source" className="mb-1 block font-sans text-sm font-bold text-black">Source</label>
                    <select
                        id="tracking-source"
                        value={filters.source}
                        onChange={(e) => updateParams({ [PARAM.source]: e.target.value })}
                        className="rounded-lg border border-gray-300 px-3 py-2 font-sans text-sm focus:outline-none"
                    >
                        {SOURCE_OPTIONS.map((s) => <option key={s || "all"} value={s}>{s || "All sources"}</option>)}
                    </select>
                </div>
            </section>

            <section className="mt-3">
                <ChipSingleSelect
                    inline
                    label="Show"
                    value={filters.view}
                    onChange={(next) => updateParams({ [PARAM.view]: next === "CONFIRMED" ? "" : next })}
                    options={VIEW_OPTIONS}
                />
            </section>

            {columnsQuery.isError && (
                <p className="mt-4 rounded-xl bg-rose-50 p-4 font-sans text-sm text-rose-700">
                    Couldn&apos;t load the column catalog: {columnsQuery.error?.message}
                </p>
            )}

            {tab === "sheet" && (
                <>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 font-sans">
                        <div className="flex flex-wrap items-center gap-4 text-[12px] text-gray-600">
                            <span className="font-bold text-black">
                                {ordersQuery.isLoading ? "Loading…" : `${total.toLocaleString("en-IN")} orders`}
                            </span>
                            {Object.entries(AVAILABILITY_META).map(([key, meta]) => (
                                <span key={key} className="inline-flex items-center gap-1.5">
                                    <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
                                    {meta.label}
                                </span>
                            ))}
                        </div>
                        <div className="flex items-center gap-3">
                            <label className="inline-flex cursor-pointer items-center gap-2 text-[12px] text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={showMissing}
                                    onChange={(e) => updateParams({ [PARAM.missing]: e.target.checked ? "" : "0" }, { keepPage: true })}
                                    className="rounded border-gray-300 text-elm-500 focus:ring-elm-500"
                                />
                                Show not-captured columns ({missingCount})
                            </label>
                            <button
                                type="button"
                                onClick={handleExport}
                                disabled={exporting || !allColumns.length}
                                className="rounded-lg bg-elm-500 px-4 py-2 text-sm font-bold text-[white] hover:bg-elm-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {exporting ? "Preparing…" : "Download (Excel)"}
                            </button>
                        </div>
                    </div>

                    {ordersQuery.isError && (
                        <p className="mt-4 rounded-xl bg-rose-50 p-4 font-sans text-sm text-rose-700">
                            Couldn&apos;t load orders: {ordersQuery.error?.message}
                        </p>
                    )}

                    {!ordersQuery.isError && sheetColumns.length > 0 && (
                        ordersQuery.isLoading ? (
                            <div className="mt-3 h-[360px] animate-pulse rounded-2xl bg-[white] shadow-soft-xl" />
                        ) : rows.length === 0 ? (
                            <div className="mt-3 rounded-2xl bg-[white] p-10 text-center font-sans text-sm text-gray-500 shadow-soft-xl">
                                No orders match these filters.
                            </div>
                        ) : (
                            <SheetTable columns={sheetColumns} rows={rows} isFetching={ordersQuery.isFetching} />
                        )
                    )}

                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 font-sans text-[12px] text-gray-600">
                        <label className="inline-flex items-center gap-2">
                            Rows per page
                            <select
                                value={size}
                                onChange={(e) => updateParams({ [PARAM.size]: e.target.value === "50" ? "" : e.target.value })}
                                className="rounded-md border border-gray-300 px-2 py-1 text-[12px]"
                            >
                                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </label>
                        {total > 0 && (
                            <Pagination
                                page={page}
                                size={size}
                                total={total}
                                onChange={(next) => updateParams({ [PARAM.page]: next === 1 ? "" : String(next) }, { keepPage: true })}
                            />
                        )}
                    </div>
                </>
            )}

            {tab === "coverage" && allColumns.length > 0 && (
                <CoveragePanel columns={allColumns} coverage={coverageQuery.data || null} isLoading={coverageQuery.isLoading} />
            )}
        </PageContainer>
    );
}

export default OrderTrackingSheet;
